import React, { useState } from "react";
import { Button, Form, Input } from "antd";
import { InputOTP } from "antd-input-otp";
import { useNavigate, useParams } from "react-router-dom";
import { useGoogleLogin } from "./store/store";
import { toast } from "react-toastify";
import trayaLogo1 from "../../../src/assets/img/illustrations/starfleet_light.png";

// Matches the redesigned /login page (antd v5 primary blue)
const PRIMARY = "#1677ff";
const PRIMARY_DARK = "#0958d9";
const OK_GREEN = "#52c41a";

const ANIM = `
  @keyframes loginSlideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

// Mirrors the backend policy in server/utils/passwordPolicy.js — the backend
// remains the source of truth (it also checks generic passwords, email
// substrings and cross-user uniqueness, which can't be validated client-side)
function hasSequentialRun(value) {
  const chars = value.toLowerCase();
  for (let i = 0; i + 2 < chars.length; i++) {
    const triple = chars.slice(i, i + 3);
    if (!/^\d{3}$/.test(triple) && !/^[a-z]{3}$/.test(triple)) continue;
    const a = chars.charCodeAt(i);
    const b = chars.charCodeAt(i + 1);
    const c = chars.charCodeAt(i + 2);
    if ((b - a === 1 && c - b === 1) || (b - a === -1 && c - b === -1)) {
      return true;
    }
  }
  return false;
}

// Small client-side mirror of the backend blocklist (backend has the full list)
const COMMON_PASSWORDS = ["password", "passw0rd", "qwerty", "welcome", "letmein", "admin", "iloveyou", "abc123", "monkey", "dragon"];
function isCommonPassword(value) {
  const lowered = value.toLowerCase();
  return COMMON_PASSWORDS.includes(lowered) || lowered.includes("password") || lowered.includes("traya");
}

// "Must have" rules: grey until met, green when met. "match" flags RED on an
// active mismatch (confirm typed but different).
const MUST_HAVE_RULES = [
  { key: "len", label: "At least 8 characters", state: (v) => (v.length >= 8 ? "ok" : null) },
  { key: "case", label: "Uppercase and lowercase letters", state: (v) => (/[a-z]/.test(v) && /[A-Z]/.test(v) ? "ok" : null) },
  { key: "num", label: "At least one number", state: (v) => (/\d/.test(v) ? "ok" : null) },
  { key: "special", label: "At least one special character (e.g. @ # !)", state: (v) => (/[^A-Za-z0-9]/.test(v) ? "ok" : null) },
  { key: "match", label: "Both passwords match", state: (v, v2) => (v2.length === 0 ? null : v === v2 ? "ok" : "bad") },
];

// "Must not have" rules: green while clean, RED the moment they're violated.
// Email-substring and cross-user uniqueness can only be checked server-side —
// shown as submit-only rows so the user knows they're coming.
const MUST_NOT_RULES = [
  { key: "seq", label: "Sequences like abc, 123 or 543", state: (v) => (v.length === 0 ? null : hasSequentialRun(v) ? "bad" : "ok") },
  { key: "common", label: "Common passwords (e.g. password, qwerty)", state: (v) => (v.length === 0 ? null : isCommonPassword(v) ? "bad" : "ok") },
  { key: "email", label: "Parts of your email address", submitOnly: true },
  { key: "unique", label: "A password another user already uses", submitOnly: true },
];

function RuleRow({ label, state, submitOnly }) {
  const color = state === "bad" ? "#cf1322" : state === "ok" ? "rgba(0,0,0,.75)" : "#8c8c8c";
  const dotColor = state === "bad" ? "#ff4d4f" : state === "ok" ? OK_GREEN : "transparent";
  const dotBorder = state === "bad" ? "#ff4d4f" : state === "ok" ? OK_GREEN : "#d9d9d9";
  return (
    <div
      className="d-flex align-items-center gap-2"
      style={{ fontSize: ".82rem", color, fontWeight: state === "bad" ? 600 : 400, padding: ".14rem 0", transition: "color .2s" }}
    >
      <span
        style={{
          width: 15,
          height: 15,
          borderRadius: "50%",
          flexShrink: 0,
          border: `1.5px ${submitOnly ? "dashed" : "solid"} ${dotBorder}`,
          background: dotColor,
          color: "#fff",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: ".62rem",
          transition: "all .2s",
        }}
      >
        {state === "ok" ? "✓" : state === "bad" ? "✕" : ""}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      {submitOnly && (
        <span style={{ flexShrink: 0, fontSize: ".62rem", fontWeight: 600, color: "#8c8c8c", background: "#f0f0f0", borderRadius: 999, padding: ".1rem .45rem" }}>
          checked on submit
        </span>
      )}
    </div>
  );
}

function sectionTitleStyle(extra) {
  return { fontSize: ".78rem", fontWeight: 700, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".5rem", ...extra };
}

function PolicyChecklist({ password, confirmPassword }) {
  return (
    <div
      style={{
        background: "#fafafa",
        border: "1px solid #f0f0f0",
        borderRadius: 8,
        padding: ".75rem .9rem",
        marginBottom: "1rem",
      }}
    >
      <div style={sectionTitleStyle()}>Must have</div>
      {MUST_HAVE_RULES.map((rule) => (
        <RuleRow key={rule.key} label={rule.label} state={rule.state(password, confirmPassword)} />
      ))}
      <div style={sectionTitleStyle({ marginTop: ".8rem" })}>Must not have</div>
      {MUST_NOT_RULES.map((rule) => (
        <RuleRow
          key={rule.key}
          label={rule.label}
          state={rule.submitOnly ? null : rule.state(password)}
          submitOnly={rule.submitOnly}
        />
      ))}
    </div>
  );
}

function policyPasses(password, confirmPassword) {
  return (
    MUST_HAVE_RULES.every((r) => r.state(password, confirmPassword) === "ok") &&
    MUST_NOT_RULES.filter((r) => !r.submitOnly).every((r) => r.state(password) === "ok")
  );
}

function ResetPassword() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { resetPassword, sendResetPasswordOtp } = useGoogleLogin();

  const [step, setStep] = useState("password"); // "password" | "otp" | "done"
  const [passwordData, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpValue, setOtpValue] = useState([]);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const policyPassed = policyPasses(passwordData, confirmPassword);

  const handleSendOtp = async () => {
    if (!policyPassed) return;
    setIsSendingOtp(true);
    const { success, devOtp, phone, email } = await sendResetPasswordOtp(id, toast);
    setIsSendingOtp(false);
    if (success) {
      setMaskedPhone(phone || "");
      setMaskedEmail(email || "");
      setOtpValue(devOtp ? devOtp.split("") : []);
      setStep("otp");
    }
  };

  const handleVerifyAndReset = async () => {
    if (otpValue?.length !== 6 || otpValue.some((d) => !d)) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }
    setIsVerifying(true);
    const success = await resetPassword(
      id,
      { password: passwordData, otp: otpValue.join("") },
      toast,
      navigate
    );
    setIsVerifying(false);
    if (success) {
      setStep("done");
    } else {
      setOtpValue([]);
    }
  };

  return (
    <div
      className="d-flex flex-column justify-content-center align-items-center vh-100"
      style={{ backgroundColor: "#f0f2f5" }}
    >
      <style>{ANIM}</style>

      <div
        className="card shadow-sm"
        style={{
          width: "100%",
          maxWidth: "26rem",
          borderRadius: 12,
          border: "1px solid #e8e8e8",
          overflow: "hidden",
          animation: "loginSlideUp .45s ease both",
        }}
      >
        {/* Accent bar — same as /login */}
        <div style={{ height: 4, background: `linear-gradient(90deg, ${PRIMARY_DARK}, ${PRIMARY})` }} />

        <div className="card-body p-4">
          {/* Logo — same treatment as /login */}
          <div className="text-center mb-3">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 80, height: 80, borderRadius: "50%", background: "#344050" }}
            >
              <img src={trayaLogo1} alt="Traya" style={{ width: 52, filter: "brightness(0) invert(1)" }} />
            </div>
            <div>
              <span
                className="badge"
                style={{ background: "rgba(22,119,255,.12)", color: PRIMARY, fontWeight: 600, fontSize: ".72rem", letterSpacing: ".5px", textTransform: "uppercase", padding: ".3rem .65rem", borderRadius: 999 }}
              >
                Password reset
              </span>
            </div>
          </div>

          {/* key= replays the slide animation on step switch */}
          <div key={step} style={{ animation: "loginSlideUp .25s ease both" }}>
            {step === "password" && (
              <>
                <div className="text-center mb-3">
                  <div className="fw-bold mb-1" style={{ fontSize: "1rem", color: "#344050" }}>Set a new password</div>
                  <div className="text-muted" style={{ fontSize: ".85rem" }}>Choose a strong password you haven't used before</div>
                </div>
                <Form layout="vertical" onFinish={handleSendOtp} requiredMark={false}>
                  <Form.Item label="New password" style={{ marginBottom: "1rem" }}>
                    <Input.Password
                      placeholder="New password"
                      autoComplete="new-password"
                      value={passwordData}
                      onChange={(e) => setPassword(e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item label="Confirm new password" style={{ marginBottom: "1rem" }}>
                    <Input.Password
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      size="large"
                    />
                  </Form.Item>

                  <PolicyChecklist password={passwordData} confirmPassword={confirmPassword} />

                  <Button
                    htmlType="submit"
                    size="large"
                    block
                    loading={isSendingOtp}
                    disabled={!policyPassed}
                    style={policyPassed ? { background: PRIMARY, borderColor: PRIMARY, color: "#fff", fontWeight: 600 } : { fontWeight: 600 }}
                  >
                    Verify via OTP &rarr;
                  </Button>
                </Form>
              </>
            )}

            {step === "otp" && (
              <>
                <div className="text-center mb-3">
                  <div className="fw-bold mb-1" style={{ fontSize: "1rem", color: "#344050" }}>Enter verification code</div>
                  <div className="text-muted" style={{ fontSize: ".85rem" }}>
                    {/* Destinations reflect the backend's RESET_PASSWORD_OTP_CHANNELS config */}
                    We've sent a 6-digit OTP to{" "}
                    {maskedPhone && <>your registered mobile <b>{maskedPhone}</b></>}
                    {maskedPhone && maskedEmail && " and "}
                    {maskedEmail && <>your email <b>{maskedEmail}</b></>}
                    {!maskedPhone && !maskedEmail && "your registered contact details"}
                  </div>
                </div>

                <InputOTP onChange={setOtpValue} value={otpValue} />

                <div className="text-center text-muted mt-2" style={{ fontSize: ".82rem" }}>
                  The code is valid for <b style={{ color: "#344050" }}>5 minutes</b>
                </div>

                <div className="text-center my-3" style={{ fontSize: ".85rem" }}>
                  Didn't get it?{" "}
                  <a
                    href="#"
                    style={{ color: PRIMARY_DARK, fontWeight: 600, textDecoration: "none" }}
                    onClick={(e) => { e.preventDefault(); if (!isSendingOtp && !isVerifying) handleSendOtp(); }}
                  >
                    {isSendingOtp ? "Sending…" : "Resend OTP"}
                  </a>
                </div>

                <div className="d-flex gap-2">
                  <Button size="large" onClick={() => setStep("password")} disabled={isVerifying}>
                    Back
                  </Button>
                  <Button
                    block
                    size="large"
                    loading={isVerifying}
                    disabled={otpValue?.length !== 6 || otpValue.some((d) => !d)}
                    style={{ background: PRIMARY, borderColor: PRIMARY, color: "#fff", fontWeight: 600 }}
                    onClick={handleVerifyAndReset}
                  >
                    Verify &amp; reset password
                  </Button>
                </div>
              </>
            )}

            {step === "done" && (
              <div className="text-center" style={{ padding: ".8rem 0 .4rem" }}>
                <div
                  className="d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(82,196,26,.12)", color: OK_GREEN, fontSize: "1.9rem" }}
                >
                  ✓
                </div>
                <div className="fw-bold mb-1" style={{ fontSize: "1rem", color: "#344050" }}>Password reset successfully</div>
                <div className="text-muted" style={{ fontSize: ".85rem" }}>Redirecting you to login&hellip;</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="text-muted mt-3" style={{ fontSize: ".8rem" }}>
        &copy; Traya Health &middot; Authorized CRM access only
      </div>
    </div>
  );
}

export default ResetPassword;
